import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  TeamSecretDetail,
  TeamSecretListItem,
  TeamSecretPolicyV1,
} from '../client';
import AsyncButton from '../components/AsyncButton';
import Modal from '../components/Modal';
import { useApi } from '../state/ApiContext';
import SecretEditor from './SecretEditor';
import SecretDetailsModal from './SecretDetailsModal';
import {
  GuardedActionButton,
  useGuardedCallback,
} from '../permissions/useGuardedAction';
import { SECRET_ACTION } from './secret-permissions';
import { useRegisterHierarchyRow } from '../components/console-hierarchy';
import './Secrets.css';

interface AgentSecretsProps {
  teamId: number;
  agentId: number;
}

interface LedgerEntry {
  item: TeamSecretListItem;
  detail?: TeamSecretDetail;
  detailError?: boolean;
}

const requestMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  fallback;

const PolicyDestination: React.FC<{
  label: string;
  enabled: boolean;
  destinations: string[];
}> = ({ label, enabled, destinations }) => (
  <div className="agent-secret-destination">
    <span className={`secret-state-chip${enabled ? ' current' : ''}`}>
      {label} · {enabled ? 'enabled' : 'disabled'}
    </span>
    {enabled && destinations.length === 0 ? (
      <span className="secret-destination-chip">No destinations allowed</span>
    ) : (
      destinations.map((destination) => (
        <span className="secret-destination-chip" key={destination}>
          {destination}
        </span>
      ))
    )}
  </div>
);

export const SafeSecretPolicy: React.FC<{ policy: TeamSecretPolicyV1 }> = ({
  policy,
}) => (
  <div className="agent-secret-policy" aria-label="Current secret policy">
    <PolicyDestination
      label="Workspace"
      enabled={policy.workspace.enabled}
      destinations={policy.workspace.allowedHosts}
    />
    <PolicyDestination
      label="Browser"
      enabled={policy.browser.enabled}
      destinations={policy.browser.allowedOrigins}
    />
  </div>
);

const AgentSecrets: React.FC<AgentSecretsProps> = ({ teamId, agentId }) => {
  const { teamSecretsApi } = useApi();
  const navigate = useNavigate();
  const { secretId: selectedSecretId = null } = useParams<{
    secretId?: string;
  }>();
  const scopeKey = `${teamId}:${agentId}`;
  const activeScopeRef = useRef(scopeKey);
  activeScopeRef.current = scopeKey;
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigningOpen, setAssigningOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [unassigningEntry, setUnassigningEntry] = useState<LedgerEntry | null>(
    null,
  );
  const [unassignError, setUnassignError] = useState('');
  const [adding, setAdding] = useState(false);
  const [partialFailureId, setPartialFailureId] = useState<string | null>(null);

  useEffect(() => {
    setAssigningOpen(false);
    setSelectedId('');
    setPendingId(null);
    setUnassigningEntry(null);
    setUnassignError('');
    setAdding(false);
    setPartialFailureId(null);
  }, [agentId, teamId]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setEntries([]);
      try {
        const response = await teamSecretsApi.listTeamSecrets(
          teamId,
          undefined,
          { signal: controller.signal },
        );
        if (cancelled) return;
        const items = response.data.items;
        setEntries(items.map((item) => ({ item })));

        let cursor = 0;
        const worker = async () => {
          while (!cancelled) {
            const index = cursor++;
            if (index >= items.length) return;
            const item = items[index];
            try {
              const detailResponse = await teamSecretsApi.getTeamSecret(
                teamId,
                item.id,
                { signal: controller.signal },
              );
              if (cancelled) return;
              setEntries((current) =>
                current.map((entry) =>
                  entry.item.id === item.id
                    ? { item: detailResponse.data, detail: detailResponse.data }
                    : entry,
                ),
              );
            } catch (detailRequestError: any) {
              if (cancelled || detailRequestError?.code === 'ERR_CANCELED')
                return;
              setEntries((current) =>
                current.map((entry) =>
                  entry.item.id === item.id
                    ? { ...entry, detailError: true }
                    : entry,
                ),
              );
            }
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(4, items.length) }, () => worker()),
        );
      } catch (listRequestError: any) {
        if (!cancelled && listRequestError?.code !== 'ERR_CANCELED') {
          setError(
            requestMessage(listRequestError, 'Could not load team secrets.'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [agentId, teamId, teamSecretsApi]);

  const isAssigned = useCallback(
    (entry: LedgerEntry) =>
      entry.detail?.grants.some((grant) => grant.agentId === agentId) === true,
    [agentId],
  );
  const unassigned = useMemo(
    () => entries.filter((entry) => entry.detail && !isAssigned(entry)),
    [entries, isAssigned],
  );
  const selectedEntry = selectedSecretId
    ? entries.find((entry) => entry.item.id === selectedSecretId)
    : undefined;
  useRegisterHierarchyRow(
    `secret:${selectedSecretId || 'none'}`,
    selectedSecretId
      ? {
          key: `secret:${selectedSecretId}`,
          kind: 'secret',
          label: `Secret: ${selectedEntry?.item.currentName || 'Secret details'}`,
          back: {
            label: 'Back to secrets',
            to: `/console/team/${teamId}/agent/${agentId}/secrets`,
          },
          testId: 'secret-hierarchy-row',
        }
      : null,
  );

  const replaceDetail = (detail: TeamSecretDetail) => {
    setEntries((current) => {
      const next = { item: detail as TeamSecretListItem, detail };
      return current.some((entry) => entry.item.id === detail.id)
        ? current.map((entry) => (entry.item.id === detail.id ? next : entry))
        : [next, ...current];
    });
  };

  const grant = async (secretId: string, created = false) => {
    const operationScope = scopeKey;
    setPendingId(secretId);
    setError('');
    try {
      const response = await teamSecretsApi.grantTeamSecret(
        teamId,
        secretId,
        agentId,
      );
      if (activeScopeRef.current !== operationScope) return;
      replaceDetail(response.data);
      setPartialFailureId(null);
      setAssigningOpen(false);
      setSelectedId('');
    } catch (grantRequestError) {
      if (activeScopeRef.current !== operationScope) return;
      if (created) setPartialFailureId(secretId);
      setError(
        created
          ? 'The secret was created, but assignment failed. Retry assignment below.'
          : requestMessage(
              grantRequestError,
              'The secret could not be assigned.',
            ),
      );
    } finally {
      if (activeScopeRef.current === operationScope) setPendingId(null);
    }
  };

  const ungrant = async (entry: LedgerEntry) => {
    const operationScope = scopeKey;
    setPendingId(entry.item.id);
    setUnassignError('');
    try {
      const response = await teamSecretsApi.ungrantTeamSecret(
        teamId,
        entry.item.id,
        agentId,
      );
      if (activeScopeRef.current !== operationScope) return;
      replaceDetail(response.data);
      setUnassigningEntry(null);
    } catch (ungrantRequestError) {
      if (activeScopeRef.current !== operationScope) return;
      setUnassignError(
        requestMessage(
          ungrantRequestError,
          'The secret could not be unassigned.',
        ),
      );
    } finally {
      if (activeScopeRef.current === operationScope) setPendingId(null);
    }
  };

  /**
   * Assigning and unassigning a team secret are both
   * `PUT/DELETE …/secrets/:id/grants/:agentId` — `TEAM_SECRET: manage` (see
   * `secret-permissions.ts`). Both directions get their own sentence because
   * "unassign" and "assign" are what the buttons say.
   */
  const assignGuard = useGuardedCallback<[string, boolean?]>({
    ...SECRET_ACTION.GRANT,
    testId: 'agent-secret-assign-denied',
    onAllowed: (secretId, created) => void grant(secretId, created),
  });
  const unassignGuard = useGuardedCallback<[LedgerEntry]>({
    ...SECRET_ACTION.UNGRANT,
    testId: 'agent-secret-unassign-denied',
    onAllowed: (entry) => void ungrant(entry),
  });

  return (
    <section className="team-secrets agent-secrets">
      <header className="team-secrets-header">
        <div>
          <span className="secret-eyebrow">Assignment ledger</span>
          <h2>Secrets</h2>
          <p>
            Grant this agent only the team credentials it needs. Values remain
            write-only in the team vault.
          </p>
        </div>
        <div className="agent-secrets-header-actions">
          {/* Both are guarded at the door as well as on the action itself:
              picking a secret from a list, or filling in a create form, only
              to be refused at the end is the failure this replaces. */}
          <GuardedActionButton
            {...SECRET_ACTION.GRANT}
            testId="agent-secret-assign-open-denied"
            className="lightButton"
            data-testid="agent-secret-assign-open"
            onAllowed={() => setAssigningOpen(true)}>
            Assign secret
          </GuardedActionButton>
          <GuardedActionButton
            {...SECRET_ACTION.CREATE}
            testId="agent-secret-create-open-denied"
            data-testid="agent-secret-create-open"
            onAllowed={() => setAdding(true)}>
            Add and assign
          </GuardedActionButton>
        </div>
      </header>

      {error && (
        <p className="secret-error" role="status">
          {error}
        </p>
      )}
      {loading && entries.length === 0 ? (
        <p className="descriptionText">Loading assignment ledger…</p>
      ) : entries.length === 0 ? (
        <div className="secret-detail-placeholder">
          <strong>No team secrets yet</strong>
          <p>
            Add one here to create it in the team vault and assign it to this
            agent.
          </p>
        </div>
      ) : (
        <div className="agent-secret-ledger">
          {entries.map((entry) => {
            const assigned = isAssigned(entry);
            return (
              <article className="agent-secret-row" key={entry.item.id}>
                <div className="agent-secret-identity">
                  <button
                    type="button"
                    className="secret-name-button"
                    onClick={() =>
                      navigate(
                        `/console/team/${teamId}/agent/${agentId}/secrets/${encodeURIComponent(entry.item.id)}`,
                      )
                    }>
                    <code>{entry.item.currentName}</code>
                  </button>
                  <span
                    className={`secret-state-chip${assigned ? ' current' : ''}`}>
                    {entry.detail
                      ? assigned
                        ? 'Assigned'
                        : 'Not assigned'
                      : entry.detailError
                        ? 'Assignment unavailable'
                        : 'Checking assignment…'}
                  </span>
                  <p>{entry.item.description}</p>
                </div>
                <SafeSecretPolicy policy={entry.item.policy} />
                <div className="agent-secret-actions">
                  {assigned ? (
                    <GuardedActionButton
                      {...SECRET_ACTION.UNGRANT}
                      testId={`agent-secret-unassign-open-denied-${entry.item.id}`}
                      as={AsyncButton}
                      className="lightButton"
                      disabled={pendingId !== null}
                      loading={pendingId === entry.item.id}
                      loadingText="Unassigning…"
                      onAllowed={() => {
                        setUnassignError('');
                        setUnassigningEntry(entry);
                      }}>
                      Unassign
                    </GuardedActionButton>
                  ) : partialFailureId === entry.item.id ? (
                    <AsyncButton
                      type="button"
                      disabled={pendingId !== null}
                      loading={pendingId === entry.item.id}
                      loadingText="Assigning…"
                      onClick={() => assignGuard.run(entry.item.id, true)}>
                      Retry assignment
                    </AsyncButton>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={assigningOpen}
        onClose={() => {
          setAssigningOpen(false);
          setSelectedId('');
        }}
        title="Assign team secret"
        isBusy={pendingId !== null}>
        <div className="secret-editor">
          <label>
            Team secret
            <select
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}>
              <option value="">Choose an unassigned secret</option>
              {unassigned.map((entry) => (
                <option key={entry.item.id} value={entry.item.id}>
                  {entry.item.currentName}
                </option>
              ))}
            </select>
          </label>
          <div className="secret-modal-actions">
            <button
              type="button"
              className="lightButton"
              onClick={() => setAssigningOpen(false)}>
              Cancel
            </button>
            <AsyncButton
              type="button"
              disabled={!selectedId || pendingId !== null}
              loading={pendingId === selectedId}
              loadingText="Assigning…"
              onClick={() => assignGuard.run(selectedId)}>
              Assign
            </AsyncButton>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={unassigningEntry !== null}
        onClose={() => {
          setUnassigningEntry(null);
          setUnassignError('');
        }}
        title={`Unassign ${unassigningEntry?.item.currentName ?? 'secret'}?`}
        isBusy={
          unassigningEntry !== null && pendingId === unassigningEntry.item.id
        }>
        {unassigningEntry && (
          <div className="secret-editor">
            <p>
              Future workspace and browser use stops immediately for this agent.
              The team secret and its other assignments are unchanged.
            </p>
            {unassignError && (
              <p className="secret-error" role="status">
                {unassignError}
              </p>
            )}
            <div className="secret-modal-actions">
              <button
                type="button"
                className="lightButton"
                disabled={pendingId !== null}
                onClick={() => {
                  setUnassigningEntry(null);
                  setUnassignError('');
                }}>
                Cancel
              </button>
              <AsyncButton
                type="button"
                className="delete-button"
                disabled={pendingId !== null}
                loading={pendingId === unassigningEntry.item.id}
                loadingText="Unassigning…"
                onClick={() => unassignGuard.run(unassigningEntry)}>
                Unassign secret
              </AsyncButton>
            </div>
          </div>
        )}
      </Modal>

      <SecretEditor
        teamId={teamId}
        isOpen={adding}
        onClose={() => setAdding(false)}
        onSaved={(secret) => {
          if (activeScopeRef.current !== scopeKey) return;
          setAdding(false);
          replaceDetail(secret);
          // Through the same guard as the buttons: a create that succeeded
          // already proves the grant, but routing the follow-up assignment
          // around the check would leave one unguarded path to the PUT.
          assignGuard.run(secret.id, true);
        }}
      />

      <SecretDetailsModal
        teamId={teamId}
        secretId={selectedSecretId}
        onClose={() =>
          navigate(`/console/team/${teamId}/agent/${agentId}/secrets`, {
            replace: true,
          })
        }
        onChanged={replaceDetail}
        onDeleted={(secretId) =>
          setEntries((current) =>
            current.filter((entry) => entry.item.id !== secretId),
          )
        }
      />

      {assignGuard.denialModal}
      {unassignGuard.denialModal}
    </section>
  );
};

export default AgentSecrets;
